import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PairingCategory as PairingCategoryType, getCategoryById } from '../pairings-data'
import { pairingsApi } from '../api/pairingsApi'
import { buildGoogleFontsUrl } from '../fonts'
import Header from './Header'
import StandaloneHeader from '../../font-pairings/components/Header'
import { useBasePath } from '../context/BasePathContext'
import PairingCard from './PairingCard'

const DEFAULT_SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog'

interface PairingCategoryProps {
  isPairingFavorited?: (numericId: number) => boolean
  togglePairing?: (numericId: number) => void
  authenticated?: boolean
}

export default function PairingCategory({ isPairingFavorited, togglePairing, authenticated }: PairingCategoryProps = {}) {
  const basePath = useBasePath()
  const isStandalone = basePath === '/font-pairings'
  const { categoryId } = useParams<{ categoryId: string }>()
  const [category, setCategory] = useState<PairingCategoryType | null | undefined>(
    () => getCategoryById(categoryId || '') ?? undefined
  )
  const [loading, setLoading] = useState(true)

  const [sampleText, setSampleText] = useState(DEFAULT_SAMPLE_TEXT)
  const [displayFontSize, setDisplayFontSize] = useState(36)
  const [bodyFontSize, setBodyFontSize] = useState(18)
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())

  // Fetch category from API
  useEffect(() => {
    if (!categoryId) {
      setLoading(false)
      return
    }

    pairingsApi
      .getCategory(categoryId)
      .then((apiCategory) => {
        if (apiCategory) {
          setCategory(apiCategory)
        } else if (!category) {
          setCategory(null)
        }
      })
      .catch(() => {
        // API unavailable — keep hardcoded fallback if available
        if (!category) setCategory(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [categoryId])

  // Load all fonts for this category
  useEffect(() => {
    if (!category) return

    const fontsToLoad = category.pairings.flatMap(p => [p.displayFont, p.bodyFont])
    const uniqueFonts = Array.from(new Set(fontsToLoad))
    const newFonts = uniqueFonts.filter(f => !loadedFonts.has(f))

    if (newFonts.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(newFonts)
      document.head.appendChild(link)

      setLoadedFonts(prev => {
        const next = new Set(prev)
        newFonts.forEach(f => next.add(f))
        return next
      })
    }
  }, [category, loadedFonts])

  const HeaderComponent = isStandalone ? StandaloneHeader : Header

  if (loading && !category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderComponent />
        <main className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="h-4 bg-gray-100 rounded w-64" />
            <div className="grid gap-3 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
                  <div className="h-10 bg-gray-100 rounded mb-3" />
                  <div className="h-6 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderComponent />
        <main className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h2>
          <p className="text-gray-600 mb-6">The requested pairing category doesn't exist.</p>
          <Link
            to={basePath}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to all pairings
          </Link>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderComponent subtitle={`${category.emoji} ${category.name}`} />

      {/* Subheader with back link */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to={basePath}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-3"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to all pairings
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl" role="img" aria-label={category.name}>
              {category.emoji}
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
              <p className="text-gray-600">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-6 items-end">
            {/* Sample Text */}
            <div className="flex-1 min-w-[250px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sample Text</label>
              <input
                type="text"
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                placeholder="Type to preview..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Display Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Display Size: {displayFontSize}px
              </label>
              <input
                type="range"
                min="24"
                max="72"
                value={displayFontSize}
                onChange={(e) => setDisplayFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>

            {/* Body Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Body Size: {bodyFontSize}px
              </label>
              <input
                type="range"
                min="12"
                max="32"
                value={bodyFontSize}
                onChange={(e) => setBodyFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pairings Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid gap-3 md:grid-cols-2">
          {category.pairings.map((pairing) => (
            <PairingCard
              key={pairing.id}
              pairing={pairing}
              sampleText={sampleText || DEFAULT_SAMPLE_TEXT}
              displayFontSize={displayFontSize}
              bodyFontSize={bodyFontSize}
              isLoaded={
                loadedFonts.has(pairing.displayFont) && loadedFonts.has(pairing.bodyFont)
              }
              isFavorited={pairing.numericId ? isPairingFavorited?.(pairing.numericId) : false}
              onToggleFavorite={pairing.numericId && togglePairing ? () => togglePairing(pairing.numericId!) : undefined}
              authenticated={authenticated}
            />
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-sm text-gray-500 text-center">
            Powered by{' '}
            <a
              href="https://fonts.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline"
            >
              Google Fonts
            </a>
            {' '}· Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
